import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';

type Language = 'pt' | 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, vars?: Record<string, string>) => string;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = '@padrinho:language';

// Import translations from JSON files
const translationsPT = require('../translations/pt.json');
const translationsEN = require('../translations/en.json');
const translationsES = require('../translations/es.json');

const translations: Record<Language, any> = {
  pt: translationsPT,
  en: translationsEN,
  es: translationsES,
};

/**
 * Get nested translation value from object using dot notation
 * e.g. "auth.login.title" -> translations.auth.login.title
 */
const getNestedValue = (obj: any, path: string): string => {
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (typeof value === 'object' && value !== null && key in value) {
      value = value[key];
    } else {
      return path; // Return the key itself if not found
    }
  }
  return typeof value === 'string' ? value : path;
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('pt');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = async () => {
    try {
      // Try to get saved language from local storage
      const savedLanguage = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLanguage === 'pt' || savedLanguage === 'en' || savedLanguage === 'es') {
        setLanguageState(savedLanguage);
      } else {
        // Try to get from user profile if authenticated
        try {
          const { data } = await supabase.auth.getSession();
          if (data?.session?.user) {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('language_preference')
              .eq('id', data.session.user.id)
              .single();
            
            if (profile?.language_preference && 
                (profile.language_preference === 'pt' || 
                 profile.language_preference === 'en' || 
                 profile.language_preference === 'es')) {
              setLanguageState(profile.language_preference);
              await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, profile.language_preference);
            }
          }
        } catch (error) {
          // Supabase not available, continue with default
        }
      }
    } catch (error) {
      console.error('Error loading language:', error);
    } finally {
      setLoading(false);
    }
  };

  const setLanguage = async (lang: Language) => {
    try {
      // Save to local storage
      await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      setLanguageState(lang);
      console.log('✅ Language changed to:', lang);

      // If user is authenticated, also sync to profile
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.user) {
          await supabase
            .from('user_profiles')
            .update({ language_preference: lang })
            .eq('id', data.session.user.id);
        }
      } catch (error) {
        // Supabase not available (e.g., during app startup), continue without sync
      }
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  const t = (key: string, vars?: Record<string, string>): string => {
    const translationObj = translations[language];
    if (!translationObj) {
      console.error('❌ Translation object not found for language:', language);
      return key;
    }
    
    let translation = getNestedValue(translationObj, key);
    
    // Debug: Log translation lookup
    if (translation === key) {
      console.warn(`⚠️ Translation not found for key: "${key}" in language: "${language}"`);
    }
    
    // Replace variables like {emotion}, {category}, etc.
    if (vars && typeof translation === 'string') {
      Object.keys(vars).forEach(varKey => {
        translation = translation.replace(new RegExp(`\\{${varKey}\\}`, 'g'), vars[varKey]);
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
